import { useEffect } from 'react';
import ReactDOM from 'react-dom';

type LoadDOMComponentArgs = {
	selector: string;
	callback: (args: { container: Element; context?: any; props?: any }) => void;
};

function parseJSONFrom(container: Element, attr: string) {
	const el = container.querySelector(`[${attr}]`);
	if (!el) return undefined;

	const raw = el.textContent?.trim();
	if (!raw) return undefined;

	try {
		return JSON.parse(raw);
	} catch (e) {
		console.log(`Invalid JSON in [${attr}]`, { attr, raw, e });
		return undefined;
	}
}

function propsFromAttributes(container: Element) {
	if (!(container instanceof HTMLElement)) return undefined;

	const out: Record<string, any> = {};

	for (const a of Array.from(container.attributes)) {
		const name = a.name;

		if (
			name === 'style' ||
			name === 'react-loaded' ||
			name === 'skipload' ||
			name === 'class'
		) {
			continue;
		}

		out[name] = a.value;
	}

	return Object.keys(out).length ? out : undefined;
}

export function loadDOMComponent({ selector, callback }: LoadDOMComponentArgs) {
	function load() {
		document.querySelectorAll(selector).forEach(container => {
			if (
				container.getAttribute('react-loaded') ||
				container.hasAttribute('skipload')
			) {
				return;
			}

			container.setAttribute('react-loaded', 'true');

			let props = parseJSONFrom(container, 'piciprops');
			const context = parseJSONFrom(container, 'context');

			if (props === undefined) {
				props = propsFromAttributes(container);
			}

			callback({
				container,
				context,
				props,
			});
		});
	}

	function reload() {
		document.querySelectorAll(selector).forEach(container => {
			container.removeAttribute('react-loaded');
			ReactDOM.unmountComponentAtNode(container);
		});

		load();
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', load, { once: true });
	} else {
		load();
	}

	document.addEventListener('nette-snippets', load);
	document.addEventListener('DOMContentLoadedForce', reload);
}

export function useEffectAsync(op: () => Promise<any>, deps: any = []) {
	useEffect(() => {
		(async () => {
			try {
				await op();
			} catch (e) {
				console.log({ e });
			}
		})();
	}, deps ?? []);
}
